/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Container, Button, ButtonGroup, Alert, Spinner, Card, Modal, Form, Row, Col, Nav, Badge } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';

import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import { WelcomeTour } from './WelcomeTour';
import { trackEvent } from '../lib/appInsights';


function QuestionnaireTable({ jwt, onLogout, onOpenQuestionnaire, isDynamicsMode }) {
    const [questionnaires, setQuestionnaires] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({ title: '', body: '', onConfirm: () => { } });
    
    // Settings & Tour State
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [tourTrigger, setTourTrigger] = useState(0);

    // Share Modal State
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareConfig, setShareConfig] = useState({ email: '', role: 2, users: [], currentSharedCount: 0, maxShareLimit: 5 });
    const [showShareConfirmation, setShowShareConfirmation] = useState(false);
    const [shareError, setShareError] = useState(null);
    const [activeTab, setActiveTab] = useState('my');
    const [isUsersLoading, setIsUsersLoading] = useState(false);


    const { SearchBar } = Search;

    const jwtRef = useRef(jwt);
    useEffect(() => {
        jwtRef.current = jwt;
    }, [jwt]);

    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError(null);
            }, 8000);
            return () => clearTimeout(timer);
        }
    }, [error]);

    const fetchQuestionnaires = useCallback(async () => {

        console.log('Attempting to fetch questionnaires...');
        if (!jwt) {
            console.log('Fetch aborted: No JWT token.');
            return;
        }

        const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });

        try {
            const response = await api.get('/api/getQuestionnaires');
            console.log('Successfully fetched questionnaires:', response.data);
            setQuestionnaires(response.data);
        } catch (err) {
            console.error('--- FETCH FAILED ---', err);
            setError(err.response?.data?.message || 'Failed to fetch questionnaires. Check console for details.');
        } finally {
            setIsLoading(false);
        }
    }, [jwt]);

    useEffect(() => {
        fetchQuestionnaires();
    }, [fetchQuestionnaires]);

    const downloadPdf = (base64String, fileName) => {
        try {
            const byteCharacters = atob(base64String);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName || 'questionnaire.pdf');
            document.body.appendChild(link);
            link.click();

            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Failed to decode or download PDF", e);
            setError("Failed to process PDF for download. The file may be corrupt.");
        }
    };

    const performClone = async () => {
        if (!selectedRow) return;
        setIsActionLoading(true);
        setError(null);
        try {
            const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });
            await api.post('/api/cloneQuestionnaire', { submissionid: selectedRow.id });

            trackEvent('QuestionnaireCloned');
            setSelectedRow(null);
            await fetchQuestionnaires();
        } catch (err) {
            console.error('--- CLONE FAILED ---', err);
            setError(err.response?.data?.message || 'Failed to clone questionnaire.');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleRefresh = async () => {
        setIsLoading(true);
        await fetchQuestionnaires();
    };

    const performExport = async () => {
        if (!selectedRow) return;
        setIsActionLoading(true);
        setError(null);
        try {
            const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });
            const response = await api.post('/api/exportQuestionnaire', { submissionid: selectedRow.id });
            const { pdfBase64, fileName } = response.data;
            if (pdfBase64) {
                trackEvent('QuestionnaireExported');
                downloadPdf(pdfBase64, fileName);
            } else {
                throw new Error("No PDF data received from server.");
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to export questionnaire.');
        } finally {
            setIsActionLoading(false);
        }
    };

    const performCreateNew = async () => {
        setIsActionLoading(true);
        setError(null);

        try {
            const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });

            console.log('Attempting to create new questionnaire...');
            const response = await api.get('/api/createQuestionnaire');

            const { recordId } = response.data;

            console.log('Creation successful, navigating to:', recordId);
            onOpenQuestionnaire(recordId, 1);

        } catch (err) {
            console.error('--- CREATE FAILED ---', err);
            setError(err.response?.data?.message || 'Failed to create new questionnaire.');
            setIsActionLoading(false);
        }
    };



    const fetchQuestionnaireUsers = async (submissionId) => {
        if (!submissionId) return;
        setIsUsersLoading(true);
        try {
            const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });
            const response = await api.get(`/api/getQuestionnaireUsers/${submissionId}`);
            const data = response.data || {};
            setShareConfig(prev => ({
                ...prev,
                users: data.users || [],
                currentSharedCount: data.currentSharedCount ?? 0,
                maxShareLimit: data.maxShareLimit ?? 5
            }));
        } catch (err) {
            console.error("Failed to fetch users:", err);
        } finally {
            setIsUsersLoading(false);
        }
    };

    const closeShareModal = () => {
        setShowShareModal(false);
        setShareError(null);
    };

    const handleShare = () => {
        if (!selectedRow) return;
        setShareConfig({ email: '', role: 2, users: [], currentSharedCount: 0, maxShareLimit: 5 }); // Reset
        setShowShareConfirmation(false);
        fetchQuestionnaireUsers(selectedRow.id);
        setShowShareModal(true);
    };

    const handleUpdateUserRole = async (user) => {
        setIsActionLoading(true);
        try {
            const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });
            await api.post('/api/updateQuestionnaireUserRole', {
                submissionId: selectedRow.id,
                email: user.email,
                role: parseInt(user.role)
            });
            trackEvent('UserRoleChanged', { role: parseInt(user.role) });
            await fetchQuestionnaireUsers(selectedRow.id);
            // alert("Role updated successfully!");
        } catch (err) {
            console.error("Failed to update user role:", err);
            alert("Failed to update user role. " + (err.response?.data?.message || err.message));
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleRemoveUser = async (user) => {
        if (!window.confirm(`Are you sure you want to remove access for ${user.email}?`)) return;

        setIsActionLoading(true);
        try {
            const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });
            await api.post('/api/removeQuestionnaireUser', {
                submissionId: selectedRow.id,
                email: user.email
            });
            trackEvent('QuestionnaireUnshared');
            // Refresh list
            await fetchQuestionnaireUsers(selectedRow.id);
        } catch (err) {
            console.error("Failed to remove user:", err);
            alert("Failed to remove user. " + (err.response?.data?.message || err.message));
        } finally {
            setIsActionLoading(false);
        }
    };

    const submitShare = async () => {
        setIsActionLoading(true);
        setShareError(null);
        try {
            const api = axios.create({ headers: { Authorization: `Bearer ${jwtRef.current}` } });
            await api.post('/api/shareQuestionnaire', {
                submissionId: selectedRow.id,
                email: shareConfig.email,
                role: parseInt(shareConfig.role)
            });
            trackEvent('QuestionnaireShared', { role: parseInt(shareConfig.role) });
            // Clear input but keep modal open and refresh list
            setShareConfig(prev => ({ ...prev, email: '' }));
            setShowShareConfirmation(false); // Reset to input view
            await fetchQuestionnaireUsers(selectedRow.id); // Refresh list and counts
            // alert("Questionnaire shared successfully!");
        } catch (err) {
            console.error('--- SHARE FAILED ---', err);
            const errorMessage = err.response?.data?.message || err.message;
            setShareError(errorMessage);
        } finally {
            setIsActionLoading(false);
        }
    };


    // Returns true if this questionnaire has never been opened by the current user
    const isNewForUser = (item) => !item.lastAccessedByUser;

    // Count unaccessed questionnaires per tab
    const myNewCount = questionnaires.filter(q => q.userRole === 1 && !q.lastAccessedByUser).length;
    const sharedNewCount = questionnaires.filter(q => q.userRole === 2 && !q.lastAccessedByUser).length;

    const columns = [
        {
            dataField: 'companyName',
            text: 'Name',
            sort: true,
            classes: 'company-name-cell',
            formatter: (cell, row) => (
                <>
                    {cell}
                    {isNewForUser(row) && (
                        <Badge bg="secondary" pill className="ms-2 new-badge">New</Badge>
                    )}
                </>
            )
        },
        { dataField: 'applicationType', text: 'Customer Type', sort: true },
        {
            dataField: 'standards',
            text: 'Standards',
            sort: true,
            headerStyle: {
                width: '25%',
                minWidth: '220px'
            }
        },
        { dataField: 'createdOn', text: 'Created On', sort: true, formatter: (cell) => new Date(cell).toLocaleDateString() },
        { dataField: 'modifiedOn', text: 'Modified On', sort: true, formatter: (cell) => new Date(cell).toLocaleDateString() },
        {
            dataField: 'submissionStatus',
            headerStyle: {
                width: '120px'
            },
            text: 'Status',
            sort: true,
            formatter: (cell) => {
                if (cell === 1) {
                    return <span className="status-in-progress">In progress</span>;
                }
                if (cell === 2) {
                    return <span className="status-completed">Completed</span>;
                }
                return <span className="status-unknown">Unknown</span>;
            }
        },
        {
            dataField: 'actions',
            text: '',
            isDummyField: true,
            align: 'right',
            headerStyle: {
                width: '120px',
                textAlign: 'right'
            },
            style: {
                width: '120px'
            },
            formatter: (cell, row) => {
                const actionText = row.submissionStatus === 1 ? 'Continue' : 'View';
                return (
                    <a href="#" className="view-link" onClick={(e) => { e.preventDefault(); onOpenQuestionnaire(row.id, row.submissionStatus); }}>
                        {actionText} <i className="fas fa-chevron-right ml-1"></i>
                    </a>
                );
            }
        }
    ];

    const selectRowProps = {
        mode: 'radio',
        clickToSelect: true,
        hideSelectColumn: false,
        classes: 'selected-row',
        selected: selectedRow ? [selectedRow.id] : [],
        onSelect: (row) => {
            if (selectedRow && selectedRow.id === row.id) {
                setSelectedRow(null);
            } else {
                setSelectedRow(row);
            }
        },
    };

    const defaultSorted = [{
        dataField: 'createdOn',
        order: 'desc'
    }];

    const paginationOptions = {
        paginationSize: 10,
        pageStartIndex: 1,
        alwaysShowAllBtns: true,
        withFirstAndLast: true,
        firstPageText: '«',
        prePageText: '‹',
        nextPageText: '›',
        lastPageText: '»',
        showTotal: true,
        hideSizePerPage: false,
        paginationTotalRenderer: (from, to, size) => (
            <span className="pagination-total">
                Showing {from}-{to} of {size}
            </span>
        )
    };


    // --- Modal and Action Handlers ---
    const handleCloseModal = () => setShowModal(false);

    const handleReplayTour = () => {
        setShowSettingsModal(false);
        setTourTrigger(prev => prev + 1);
    };

    const openConfirmationModal = (title, body, onConfirm) => {
        setModalConfig({ title, body, onConfirm });
        setShowModal(true);
    };

    const handleNew = () => {
        openConfirmationModal(
            'Create New Questionnaire',
            'Are you sure you want to create a new questionnaire?',
            performCreateNew // Point to local function
        );
    };

    const handleClone = () => {
        console.log('Clone button clicked. Opening confirmation modal for row:', selectedRow);
        if (!selectedRow) return;
        openConfirmationModal(
            'Clone Questionnaire',
            `Are you sure you want to clone "${selectedRow.companyName}"?`,
            performClone
        );
    };

    const handleExport = () => {
        openConfirmationModal(
            'Export to PDF',
            `Are you sure you want to export "${selectedRow.companyName}" to PDF?`,
            performExport
        );
    };

    // --- Render Logic ---
    if (isLoading && questionnaires.length === 0) {
        return <Spinner animation="border" variant="primary" style={{ margin: '20px' }} />;
    }


    const filteredQuestionnaires = questionnaires.filter(q => {
        if (activeTab === 'my') {
            return q.userRole === 1; // Owner
        } else {
            return q.userRole === 2; // Contributor
        }
    });

    return (
        <>
            <Container fluid>
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">
                        <p className="mb-0">{error}</p>
                    </Alert>
                )}

                <Card className='questionnaire-table-card'>
                    <Card.Body>
                        <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => { setActiveTab(k); setSelectedRow(null); }} className="mb-3" data-tour="questionnaire-tabs">
                            <Nav.Item>
                                <Nav.Link eventKey="my">
                                    My Questionnaires{' '}
                                    {myNewCount > 0 && (
                                        <Badge bg="secondary" pill className="ms-1 new-badge">{myNewCount} New</Badge>
                                    )}
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="shared">
                                    Shared with me{' '}
                                    {sharedNewCount > 0 && (
                                        <Badge bg="secondary" pill className="ms-1 new-badge">{sharedNewCount} New</Badge>
                                    )}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                        <ToolkitProvider
                            keyField="id"
                            data={filteredQuestionnaires}
                            columns={columns}
                            search
                        >
                            {
                                props => (
                                    <div style={{ position: 'relative' }}>
                                        {isLoading && questionnaires.length > 0 && (
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(255,255,255,0.7)',
                                                zIndex: 100,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}>
                                                <Spinner animation="border" variant="primary" />
                                            </div>
                                        )}

                                        <div className="questionnaire-toolbar">

                                            <div className="session-header">
                                                <h5 className="session-title">Questionnaires</h5>
                                                <span className="session-count">
                                                    {questionnaires.length}
                                                    {questionnaires.length !== 1 ? " Questionnaires" : " Questionnaire"}
                                                </span>
                                            </div>

                                            <div className="questionnaire-toolbar-right">
                                                <div data-tour="questionnaire-search">
                                                    <SearchBar
                                                        {...props.searchProps}
                                                        className="table-search-bar"
                                                        placeholder="Search..."
                                                    />
                                                </div>
                                                <ButtonGroup className='table-button-group' data-tour="questionnaire-actions">
                                                    <Button 
                                                        className="questionnaire-action-button" 
                                                        variant="outline-secondary" 
                                                        onClick={() => setShowSettingsModal(true)} 
                                                        title="Settings"
                                                    >
                                                        <i className="fas fa-cog"></i>
                                                    </Button>
                                                    <Button className="questionnaire-action-button" variant="outline-secondary" onClick={handleRefresh} disabled={isLoading} title="Refresh Table">
                                                        <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
                                                    </Button>
                                                    <Button className="questionnaire-action-button" variant="secondary" onClick={handleClone} disabled={!selectedRow || isActionLoading}>
                                                        {isActionLoading ? <Spinner as="span" animation="border" size="sm" /> : <i className="fas fa-copy mr-2"></i>} Clone
                                                    </Button>
                                                    {/* Share Button - Only visible for 'my' questionnaires (Owners) */}
                                                    {activeTab === 'my' && (
                                                        <Button className="questionnaire-action-button" variant="secondary" onClick={handleShare} disabled={!selectedRow || isActionLoading}>
                                                            {isActionLoading ? <Spinner as="span" animation="border" size="sm" /> : <i className="fas fa-share-alt mr-2"></i>} Share
                                                        </Button>
                                                    )}
                                                    <Button className="questionnaire-action-button" variant="secondary" onClick={handleExport} disabled={!selectedRow || selectedRow.submissionStatus !== 2 || isActionLoading}>
                                                        {isActionLoading ? <Spinner as="span" animation="border" size="sm" /> : <i className="fas fa-file-pdf mr-2"></i>} Export
                                                    </Button>
                                                    <Button
                                                        data-tour="questionnaire-new"
                                                        className="questionnaire-action-button"
                                                        variant="primary"
                                                        onClick={handleNew}
                                                        disabled={isActionLoading}
                                                    >
                                                        {isActionLoading ? <Spinner as="span" animation="border" size="sm" /> : <i className="fas fa-plus mr-2"></i>} New Questionnaire
                                                    </Button>
                                                </ButtonGroup>
                                            </div>
                                        </div>
                                        <hr />
                                        <div className="questionnaire-table-scroll-wrapper" data-tour="questionnaire-table">
                                            <BootstrapTable
                                                {...props.baseProps}
                                                classes="questionnaire-table"
                                                bootstrap4
                                                hover
                                                borderless
                                                defaultSorted={defaultSorted}
                                                pagination={paginationFactory(paginationOptions)}
                                                selectRow={selectRowProps}
                                                noDataIndication={activeTab === 'my' ? "No questionnaires found" : "No shared questionnaires found"}
                                            />
                                        </div>
                                    </div>
                                )
                            }
                        </ToolkitProvider>
                    </Card.Body>
                </Card>
            </Container>

            {/* Confirmation Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modalConfig.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modalConfig.body}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => {
                        modalConfig.onConfirm();
                        handleCloseModal();
                    }}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Share Modal */}
            <Modal show={showShareModal} onHide={closeShareModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Share Questionnaire</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {shareError && (
                        <Alert variant="danger" onClose={() => setShareError(null)} dismissible className="mb-3">
                            {shareError}
                        </Alert>
                    )}
                    {!showShareConfirmation ? (
                        <>
                            {/* Sharing Limit Indicator */}
                            {isUsersLoading ? (
                                <div className="d-flex justify-content-center py-3">
                                    <Spinner animation="border" variant="primary" size="sm" className="me-2" />
                                    <span className="text-muted">Loading sharing info...</span>
                                </div>
                            ) : (
                            <>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <small className="text-muted">
                                    <strong>{shareConfig.currentSharedCount}</strong> of <strong>{shareConfig.maxShareLimit}</strong> users currently have access to this questionnaire.
                                </small>
                                {shareConfig.currentSharedCount >= shareConfig.maxShareLimit && (
                                    <Badge bg="danger">Sharing limit reached</Badge>
                                )}
                            </div>

                            {shareConfig.currentSharedCount >= shareConfig.maxShareLimit ? (
                                <Alert variant="warning" className="mb-3">
                                    This questionnaire has reached the maximum limit of {shareConfig.maxShareLimit} users.
                                    To share with a new user, please remove an existing user first.
                                </Alert>
                            ) : (
                            <Form>
                                <Form.Group as={Row} controlId="formShareEmail">
                                    <Form.Label column sm="3">Email</Form.Label>
                                    <Col sm="9">
                                        <Form.Control
                                            type="email"
                                            placeholder="Enter collaborator's email"
                                            value={shareConfig.email}
                                            onChange={(e) => setShareConfig({ ...shareConfig, email: e.target.value })}
                                        />
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} controlId="formShareRole" className="mt-2">
                                    <Form.Label column sm="3">Role</Form.Label>
                                    <Col sm="9">
                                        <Form.Select
                                            value={shareConfig.role}
                                            onChange={(e) => setShareConfig({ ...shareConfig, role: e.target.value })}
                                        >
                                            <option value="1">Owner</option>
                                            <option value="2">Contributor</option>
                                        </Form.Select>
                                    </Col>
                                </Form.Group>
                                <div className="d-flex justify-content-end mt-3">
                                    <Button variant="primary" onClick={() => setShowShareConfirmation(true)} disabled={!shareConfig.email || isActionLoading}>
                                        {isActionLoading ? 'Sharing...' : 'Share'}
                                    </Button>
                                </div>
                            </Form>
                            )}
                            </>
                            )}

                            <hr className="my-4" />

                            <h5>Active Users</h5>
                            <div className="table-responsive" style={{ position: 'relative', minHeight: '100px' }}>
                                {(isUsersLoading || (isActionLoading && shareConfig.users.length > 0)) && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(255,255,255,0.7)',
                                        zIndex: 10,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}>
                                        <Spinner animation="border" variant="primary" />
                                    </div>
                                )}
                                <table className="table table-sm table-striped">
                                    <thead>
                                        <tr>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th style={{ width: '100px' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {shareConfig.users && shareConfig.users.length > 0 ? (
                                            shareConfig.users.map((user, index) => (
                                                <tr key={user.userId ?? user.email ?? `user-${index}`}>
                                                    <td>{user.email || <span className="text-muted">(No Email)</span>}</td>
                                                    <td>
                                                        {user.role === 1 ? (
                                                            'Owner'
                                                        ) : (
                                                            <Form.Select
                                                                size="sm"
                                                                value={user.role}
                                                                onChange={(e) => {
                                                                    const newRole = e.target.value;
                                                                    setShareConfig(prev => ({
                                                                        ...prev,
                                                                        users: prev.users.map(u =>
                                                                            (u.userId ?? u.email) === (user.userId ?? user.email) ? { ...u, role: newRole } : u
                                                                        )
                                                                    }));
                                                                }}
                                                                disabled={isActionLoading}
                                                            >
                                                                <option value="1">Owner</option>
                                                                <option value="2">Contributor</option>
                                                            </Form.Select>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex justify-content-evenly align-items-center">
                                                            {user.role !== 1 && (
                                                                <Button
                                                                    variant="outline-success"
                                                                    size="sm"
                                                                    onClick={() => handleUpdateUserRole(user)}
                                                                    disabled={isActionLoading || !user.email}
                                                                    title="Save Role"
                                                                >
                                                                    <i className="fas fa-save"></i>
                                                                </Button>
                                                            )}
                                                            {user.role !== 1 && (
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleRemoveUser(user)}
                                                                    disabled={isActionLoading || !user.email}
                                                                    title={!user.email ? "Cannot remove user without email" : "Deactivate User"}
                                                                >
                                                                    <i className="fas fa-trash-alt"></i>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>

                                            ))
                                        ) : (
                                            !isUsersLoading && (
                                                <tr>
                                                    <td colSpan="3" className="text-center">No active users found.</td>
                                                </tr>
                                            )
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <div className="p-3">
                            <h5 className="text-danger mb-3">Data Sharing Notice</h5>
                            <p className="mb-4">
                                You are about to share this Questionnaire with another user. Please ensure the recipient is authorized to access this information and that the content does not contain sensitive, personal, or restricted data that violates company data handling policies. By proceeding, you confirm that this sharing action complies with applicable data protection and confidentiality guidelines.
                            </p>
                            <div className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={() => setShowShareConfirmation(false)} className="me-2">
                                    Cancel
                                </Button>
                                <Button variant="primary" onClick={submitShare} disabled={isActionLoading}>
                                    {isActionLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Confirm Share'}
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeShareModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Settings Modal */}
            <Modal show={showSettingsModal} onHide={() => setShowSettingsModal(false)} centered size="md">
                <Modal.Header closeButton>
                    <Modal.Title>Settings</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-grid gap-3">
                        <Button variant="outline-primary" onClick={handleReplayTour} className="text-start">
                            <i className="fas fa-play-circle me-2"></i> Replay Welcome Tour
                        </Button>
                        <hr className="my-1" />
                        <Button variant="outline-danger" onClick={() => { setShowSettingsModal(false); onLogout(); }} className="text-start">
                            <i className="fas fa-sign-out-alt me-2"></i> Log Out
                        </Button>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSettingsModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Welcome tour — only rendered once the table has finished loading and not in Dynamics mode */}
            {!isDynamicsMode && <WelcomeTour isReady={!isLoading} forceTrigger={tourTrigger} />}
        </>
    );
}

export default QuestionnaireTable;